/**
 * Pagafácil · routing.
 *
 * Decisión 19/06/2026 (ADR-011): sub-marcas se sirven por path
 * (`/coordina`, `/propina`, `/oficina`, `/caseta`) y NO por subdominio.
 *
 * Compatibilidad: si el navegador aterriza en `coordina.pagafacil.eu/`,
 * detectamos el subdominio y hacemos client-side redirect al path
 * equivalente (`pagafacil.eu/coordina`). El nginx host también hace
 * 301 server-side cuando puede para no servir HTML por subdominios.
 */
import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import HomePage from './pages/HomePage';
import CreatePage from './pages/CreatePage';
import PublicPage from './pages/PublicPage';
import AdminPage from './pages/AdminPage';
import ParticipantPage from './pages/ParticipantPage';
import SharePage from './pages/SharePage';
import EmbedPage from './pages/EmbedPage';
import MyCobrosPage from './pages/MyCobrosPage';
import EntityPage from './pages/EntityPage';
import StartFlowPage from './pages/StartFlowPage';
import CasetaAdminPage from './pages/CasetaAdminPage';
import CasetaBonoPage from './pages/CasetaBonoPage';
import OperatorPage from './pages/OperatorPage';
import PricingPage from './pages/PricingPage';
import PartnerDocsPage from './pages/PartnerDocsPage';
import PublicRafflesPage from './pages/PublicRafflesPage';
import SubBrandLanding from './pages/SubBrandLanding';
import TicketsPage from './pages/TicketsPage';
import EventLandingPage from './pages/EventLandingPage';
import PurchaseSuccessPage from './pages/PurchaseSuccessPage';
import EventosAdminPage from './pages/EventosAdminPage';
import MisEntradasPage from './pages/MisEntradasPage';
import TaquillaPage from './pages/TaquillaPage';
import EntityLandingPage from './pages/EntityLandingPage';
import EntityAdminPage from './pages/EntityAdminPage';
import GuestListSignupPage from './pages/GuestListSignupPage';


/**
 * Si el usuario aterriza en un subdominio legacy (coordina.pagafacil.eu, …),
 * lo redirigimos al path equivalente del dominio principal. Mantiene la
 * URL pegada del WhatsApp viejo sin romperse.
 */
function useLegacySubdomainRedirect() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const h = window.location.hostname;
    const sub = ['coordina.', 'propina.', 'oficina.', 'caseta.'].find((p) => h.startsWith(p));
    if (!sub) return;
    const brand = sub.replace('.', '');
    const base = h.replace(sub, '');
    const target = `${window.location.protocol}//${base}/${brand}${window.location.pathname === '/' ? '' : window.location.pathname}${window.location.search}`;
    window.location.replace(target);
  }, []);
}


export default function App() {
  useLegacySubdomainRedirect();

  return (
    <Routes>
      {/* Home Pagafácil */}
      <Route path="/" element={<HomePage />} />

      {/* Sub-marcas · landings dedicadas en paths */}
      <Route path="/coordina" element={<SubBrandLanding brand="coordina" />} />
      <Route path="/propina" element={<SubBrandLanding brand="propina" />} />
      <Route path="/oficina" element={<SubBrandLanding brand="oficina" />} />

      {/* Producto */}
      <Route path="/empezar" element={<StartFlowPage />} />
      <Route path="/nuevo" element={<CreatePage />} />
      <Route path="/mis-cobros" element={<MyCobrosPage />} />
      <Route path="/precio" element={<PricingPage />} />
      <Route path="/partners" element={<PartnerDocsPage />} />
      <Route path="/sorteos-publicos" element={<PublicRafflesPage />} />

      {/* Ticketera pagafacil.eu · eventos propios (camino B) */}
      <Route path="/entradas" element={<TicketsPage />} />
      <Route path="/entradas/gracias" element={<PurchaseSuccessPage />} />
      <Route path="/e/:slug" element={<EventLandingPage />} />
      <Route path="/mis-entradas" element={<MisEntradasPage />} />

      {/* Multi-tenant fase A · landing pública por organizador */}
      <Route path="/o/:slug" element={<EntityLandingPage />} />
      {/* Multi-tenant fase B3 · admin scoped por entidad (magic link) */}
      <Route path="/o/:slug/admin" element={<EntityAdminPage />} />
      {/* Guest list público · /lista/<sale_token> del RRPP */}
      <Route path="/lista/:token" element={<GuestListSignupPage />} />

      {/* Entidades · Pagafácil Hub (Idea 9 ROADMAP) */}
      <Route path="/entidad/nueva" element={<EntityPage mode="create" />} />
      <Route path="/entidades" element={<EntityPage mode="lookup" />} />
      <Route path="/entidad/:slug/ficha" element={<EntityPage mode="edit" />} />

      {/* Caseta de feria · Idea 13 ROADMAP */}
      <Route path="/c/:slug/admin" element={<CasetaAdminPage />} />
      <Route path="/c/bono/:qr" element={<CasetaBonoPage />} />

      {/* Operador del stack (Pagafácil S.L.) · auth magic link al ADMIN_EMAIL */}
      <Route path="/operador" element={<OperatorPage />} />
      <Route path="/operador/eventos" element={<EventosAdminPage />} />
      <Route path="/operador/taquilla" element={<TaquillaPage />} />

      {/* Cobros individuales */}
      <Route path="/g/:slug" element={<PublicPage />} />
      <Route path="/g/:slug/admin" element={<AdminPage />} />
      <Route path="/g/:slug/share/:orderId" element={<SharePage />} />
      <Route path="/g/:slug/p/:accessToken" element={<ParticipantPage />} />
      <Route path="/embed/g/:slug" element={<EmbedPage />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
